import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"; 
import { Form, Button, Row, Col, Modal } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";
import SolutionStep from "./Solutionstep";
import ObjectiveRow from "./ObjectiveRow";
import AuthContext from "../../../auth/AuthContext";
import AlertService from "../../../services/AlertService";
import CatalogueService from "../../../services/CatalogueService";
import { MultiSelect } from 'primereact/multiselect';
import ConfirmDialogBox from "../../confirmDialog/ConfirmDialogBox";

const initialObjectives = [
  "Vulnerability Objective",
  "Learning Objective",
  "Technical Objective",
  "Case Study Objective",
  "Future Objective",
].map((label, i) => ({
  id: i + 1,
  label,
  text: "<p></p>",
  removed: false,
  type: i + 1,
}));

const CreateCatalogue = () => {
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  // const { incidentName } = useParams(); 
  const { state } = useLocation(); 
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [incidentId, setIncidentId] = useState(0);
  const [incidentTitle, setIncidentTitle] = useState("");
  const [incidentNo, setIncidentNo] = useState("");
  const [storyBoard, setStoryBoard] = useState("");
  const [task, setTask] = useState("");
  const [tags, setTags] = useState([]);
  const [categoryId, setCategoryId] = useState([]);
  const [objectives, setObjectives] = useState(initialObjectives);
  const [solutionSteps, setSolutionSteps] = useState([
    { id: 1, stepName: "Step 1", stepText: "", isValid: true },
  ]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const submitControllerRef = useRef(null);

  // Validation states
  const [incidentTitleIsValid, setIncidentTitleIsValid] = useState(true);
  const [incidentNoIsValid, setIncidentNoIsValid] = useState(true);
  const [storyBoardIsValid, setStoryBoardIsValid] = useState(true);
  const [taskIsValid, setTaskIsValid] = useState(true);
  const [categoryIsValid, setCategoryIsValid] = useState(true);
  const loadControllerRef = useRef(null);   

const loadIncident = useCallback(async (signal) => {
  if (!state?.id) {
    AlertService.error("Invalid incident ID");
    navigate("/catalogue-dashboard/catalogue");
    return;
  }

  setLoading(true);
  try {
    const data = await CatalogueService.Get(
      state.id,
      authContext,
      signal
    );

    if (signal?.aborted) return;

    if (data.isSuccess) {
      setIncidentTitle(data.data.incidentTitle);
      setIncidentNo(data.data.incidentNo);
      setStoryBoard(data.data.storyBoard);
      setTask(data.data.task);
      setTags(data.data.tags ? data.data.tags.split(",") : []);
      setCategoryId(data.data.categories || []);

      const updatedObjectives = initialObjectives.map((obj) => {
        const apiObj = data.data.objectives.find(
          (o) => o.objectiveType === obj.type
        );
        return {
          ...obj,
          text: apiObj ? apiObj.description : "<p></p>",
          removed: !apiObj,
        };
      });
      setObjectives(updatedObjectives);

      if (data.data.steps && data.data.steps.length > 0) {
        setSolutionSteps(
          data.data.steps.map((step) => ({
            id: step.orderBy,
            stepName: step.step,
            stepText: step.description,
            isValid: true,
          }))
        );
      }
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
    AlertService.error("Failed to load incident");
  } finally {
    if (!signal?.aborted) setLoading(false);
  }
}, [authContext, state, navigate]);

const loadCategories = useCallback(async (signal) => {
  try {
    const data = await CatalogueService.GetCategories(
      authContext,
      signal
    );

    if (signal?.aborted) return;

    if (data.isSuccess) {
      setCategories(data.data);
    } else {
      AlertService.error(data.errorMessage);
    }
  } catch (err) {
    if (err.name === "AbortError") return;
  }
}, [authContext]);

useEffect(() => {
  if (loadControllerRef.current) {
    loadControllerRef.current.abort();
  }
  const controller = new AbortController();
  loadControllerRef.current = controller;

  loadCategories(controller.signal);
  if (state?.id) {
    setIncidentId(state.id);
    loadIncident(controller.signal);
  } else {
    setLoading(false);
  }

  return () => controller.abort();
}, [loadCategories, loadIncident, state]);

  const toggleRemoveObjective = (id) => {
    setObjectives(prev => prev.map(o =>
      o.id === id ? { ...o, removed: !o.removed } : o
    ));
  };

  const changeObjectiveText = (id, newText) => {
    setObjectives(prev => prev.map(o =>
      o.id === id ? { ...o, text: newText } : o
    ));
  };

  const addStepHandler = () => {
    setSolutionSteps(prev => {
      const newId = prev.length > 0 ? Math.max(...prev.map(s => s.id)) + 1 : 1;
      return [
        ...prev,
        {
          id: newId,
          stepName: `Step ${prev.length + 1}`,
          stepText: "",
          isValid: true,
        },
      ];
    });
  };

  const removeStepHandler = (id) => {
    setSolutionSteps(prev => {
      const updated = prev.filter(s => s.id !== id);
      return updated.map((s, i) => ({
        ...s,
        id: i + 1,
        stepName: `Step ${i + 1}`,
      }));
    });
  };

  const stepValueHandler = (id, value) => {
    setSolutionSteps(prev =>
      prev.map(s =>
        s.id === id
          ? { ...s, stepText: value, isValid: !!value }
          : s
      )
    );
  };

  const validateForm = () => {
    setIncidentTitleIsValid(true);
    setIncidentNoIsValid(true);
    setStoryBoardIsValid(true);
    setTaskIsValid(true);
    setCategoryIsValid(true);

    let isValid = true;

    if (!incidentTitle) {
      setIncidentTitleIsValid(false);
      isValid = false;
    }

    if (!incidentNo) {
      setIncidentNoIsValid(false);
      isValid = false;
    }

    if (!storyBoard) {
      setStoryBoardIsValid(false);
      isValid = false;
    }

    if (!task) {
      setTaskIsValid(false);
      isValid = false;
    }

    if (!categoryId || categoryId.length === 0) {
      setCategoryIsValid(false);
      isValid = false;
    }
     // Validate objectives
    const objectivesValid = objectives.every(obj =>
      obj.removed || (obj.text && obj.text !== "<p></p>" && obj.text.trim() !== "")
    );
     // Validate solution steps

    const stepsValid = solutionSteps.every(step =>
      step.stepText && step.stepText !== "<p></p>" && step.stepText.trim() !== ""
    );

   if (!objectivesValid || !stepsValid) {
  AlertService.error(
    "Please fill all objectives and solution steps. " +
    "If any objective is not required, you can remove it by clicking the close (X) button."
  );
  isValid = false;
}

    return isValid;
  };

  const handlePreview = (e) => {
    e.preventDefault();
      setIsSubmitted(true);
     if (validateForm()) {
      setShowPreview(true);
    }
  };



const handleSubmit = async () => {
  setShowPreview(false);

  if (submitControllerRef.current) {
    submitControllerRef.current.abort();
  }
  const controller = new AbortController();
  submitControllerRef.current = controller;

  setLoading(true);

  try {
    const objectivesPayload = objectives
      .filter((obj) => !obj.removed)
      .map((obj) => ({
        objectiveType: obj.type,
        orderBy: obj.id,
        description: obj.text,
      }));

    const stepsPayload = solutionSteps.map((step) => ({
      step: step.stepName,
      orderBy: step.id,
      description: step.stepText,
    }));

    const payload = {
      incidentTitle,
      incidentNo,
      storyBoard,
      task,
      tags: tags.join(","),
      categories: categoryId,
      objectives: objectivesPayload,
      steps: stepsPayload,
    };

    if (incidentId) {
      payload.id = incidentId;
    }

    const response = await CatalogueService.Save(
      payload,
      authContext,
      controller.signal
    );

    if (controller.signal.aborted) return;

    if (response.isSuccess) {
      AlertService.success(
        `Incident ${incidentId ? "updated" : "created"} successfully!`
      );
      navigate("/catalogue-dashboard/catalogue");
    } else {
      AlertService.error(response.errorMessage);
    }
  } catch (error) {
    if (error.name === "AbortError") return;
    AlertService.error("An error occurred while saving");
    console.error(error);
  } finally {
    if (!controller.signal.aborted) setLoading(false);
  }
};

// Unmount cleanup
useEffect(() => {
  return () => {
    submitControllerRef.current?.abort();
    loadControllerRef.current?.abort();   
  };
}, []);

  const onCancelHandler = () => {
    navigate("/catalogue-dashboard/catalogue");
  };

  const onDeleteHandler = async () => {
    setShowDeleteConfirm(false);
    const response = await CatalogueService.Remove(incidentId, authContext);

    if (response.isSuccess) {
      AlertService.success("Incident deleted successfully!");
      navigate("/catalogue-dashboard/catalogue");
    } else {
      AlertService.error(response.errorMessage);
    }
  };

  const getCategoryNames = () => {
    return categoryId.map(id => {
      const category = categories.find(c => c.value === id);
      return category ? category.label : "";
    }).filter(name => name !== "");
  };

  return (
    <>
      <div className="container-fluid custom-container mt-3 mb-5">
        {incidentId > 0 && (
          <div className="d-flex justify-content-end mb-2">
            <button className="btn text-danger btn-sm me-2" onClick={() => setShowDeleteConfirm(true)}>
              <FaTrash className="me-1 fs-5 mb-1" /> Delete
            </button>
          </div>
        )}
        <Form onSubmit={handlePreview} className="ms-2">
          <Row className="mb-4">
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Category</Form.Label>
            </Col>
            <Col>
              <MultiSelect
                value={categoryId}
                options={categories}
                onChange={(e) => {
                  setCategoryId(e.target.value);
                  setCategoryIsValid(true);
                }}
                optionLabel="label"
                filter
                placeholder="Select Categories"
                className={`custom-input custom-input-color w-100 ${!categoryIsValid ? 'p-invalid' : ''}`}
                display="chip"
              />
              {!categoryIsValid && (
                <Form.Text className="text-danger">
                  Please select at least one category
                </Form.Text>
              )}
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Incident Title</Form.Label>
            </Col>
            <Col>
              <Form.Control
                className="custom-input custom-input-color"
                value={incidentTitle}
                onChange={e => {
                  setIncidentTitle(e.target.value);
                  setIncidentTitleIsValid(true);
                }}
                isInvalid={!incidentTitleIsValid}
              />
              {!incidentTitleIsValid && (
                <Form.Text className="text-danger">
                  Please provide incident title
                </Form.Text>
              )}
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Incident No</Form.Label>
            </Col>
            <Col>
              <Form.Control
                className="custom-input custom-input-color"
                value={incidentNo}
                onChange={e => {
                  setIncidentNo(e.target.value);
                  setIncidentNoIsValid(true);
                }}
                isInvalid={!incidentNoIsValid}
              />
              {!incidentNoIsValid && (
                <Form.Text className="text-danger">
                  Please provide incident number
                </Form.Text>
              )}
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Story Board</Form.Label>
            </Col>
            <Col>
              <Form.Control
                className="custom-input-color"
                as="textarea"
                rows={4}
                value={storyBoard}
                onChange={e => {
                  setStoryBoard(e.target.value);
                  setStoryBoardIsValid(true);
                }}
                isInvalid={!storyBoardIsValid}
              />
              {!storyBoardIsValid && (
                <Form.Text className="text-danger">
                  Please provide story board
                </Form.Text>
              )}
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Task</Form.Label>
            </Col>
            <Col>
              <Form.Control
                className="custom-input custom-input-color"
                value={task}
                onChange={e => {
                  setTask(e.target.value);
                  setTaskIsValid(true);
                }}
                isInvalid={!taskIsValid}
              />
              {!taskIsValid && (
                <Form.Text className="text-danger">
                  Please provide task
                </Form.Text>
              )}
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={2} className="d-flex align-items-center">
              <Form.Label className="mb-0">Tags</Form.Label>
            </Col>
            <Col>
              <Form.Control
                className="custom-input custom-input-color"
                value={tags.join(", ")}
                onChange={e => setTags(e.target.value.split(",").map(tag => tag.trim()))}
                placeholder="Tags"
              />
            </Col>
          </Row>

          <Row>
            <Col md={2} className="d-flex align-items-start pt-2">
              <Form.Label className="mb-0">5&nbsp;O's</Form.Label>
            </Col>
            <Col>
              {objectives.map(obj => (
                <ObjectiveRow
                  key={obj.id}
                  obj={obj}
                  onToggleRemove={toggleRemoveObjective}
                  onChange={changeObjectiveText}
                />
              ))}
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={2} className="d-flex align-items-start pt-2">
              <Form.Label className="mb-0">Solution</Form.Label>
            </Col>
            <Col>
              {solutionSteps.map(step => (
                <SolutionStep
                  key={step.id}
                  id={step.id}
                  stepName={step.stepName}
                  isSubmitted={isSubmitted}  
                  stepText={step.stepText}
                  isValid={step.isValid}
                  removeHandler={removeStepHandler}
                  valueHandler={stepValueHandler}
                  disableDelete={solutionSteps.length === 1}
                />
              ))}

              <div className="mt-3 d-flex justify-content-between align-items-center">
                <Button
                  variant="primary"
                  className="py-2 px-5"
                  onClick={addStepHandler}
                  disabled={loading}
                >
                  <FaPlus className="pb-1 me-1" />Add Step
                </Button>

                <div>
                  <Button
                    variant="outline-secondary"
                    className="me-3 py-2 px-5"
                    onClick={onCancelHandler}
                    disabled={loading}
                  >Cancel
                  </Button>

                  <Button
                    variant="dark"
                    className="py-2 px-5"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Submit"}
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
  {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="xl"
        centered
        backdrop="static"
        dialogClassName="modal-dialog-scrollable"
      >
        <Modal.Header className="text-center border-0" closeButton>
          <Modal.Title className="w-100 text-center">Incident Preview</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="preview-container">
            <Row className="mb-3">
              <Col md={3}><strong>Category:</strong></Col>
              <Col>{getCategoryNames().join(", ")}</Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}><strong>Incident Title:</strong></Col>
              <Col>{incidentTitle}</Col>
            </Row>

            <Row className="mb-3">
              <Col md={3}><strong>Incident No:</strong></Col>
              <Col>{incidentNo}</Col>
            </Row>

            <div className="mb-3">
              <h5>5 O's</h5>
              {objectives
                .filter(obj => !obj.removed)
                .map(obj => (
                  <Row key={obj.id} className="mb-3">
                    <Col md={3}><strong>{obj.label}:</strong></Col>
                    <Col>
                      <div
                        className="preview-content"
                        dangerouslySetInnerHTML={{ __html: obj.text }}
                      />
                    </Col>
                  </Row>
                ))}
            </div>

            <div>
              <h5>Solution Steps</h5>
              {solutionSteps.map(step => (
                <div key={step.id} className="mb-3">
                  <strong>{step.stepName}:</strong>
                  <div
                    className="preview-content"
                    dangerouslySetInnerHTML={{ __html: step.stepText }}
                  />
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 gap-2">
          <Button
            variant="secondary"
            className="py-2 px-4"
            onClick={() => setShowPreview(false)}
          >
            Back to Edit
          </Button>
          <Button
            variant="primary"
            className="py-2 px-4"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Submitting..." : "Confirm Submit"}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmDialogBox
        visible={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={onDeleteHandler}
        title="Are you sure you want to delete this incident?"
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </>
  );
};

export default CreateCatalogue;